import { supabase } from "./supabase";

class SessionService {
  currentSessionId = null;

  // Create a new session when user logs in
  async createSession(user) {
    console.log("🔵 createSession called");
    console.log("🔵 Current sessionId:", this.currentSessionId);
    console.log("🔵 User:", user.id, user.email);

    // Don't create duplicate sessions
    if (this.currentSessionId) {
      console.log("⚠️ Session already exists:", this.currentSessionId);
      return;
    }

    try {
      console.log("📝 Preparing session data...");
      
      const sessionData = {
        user_id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || null,
        ip_address: await this.getIPAddress(),
        user_agent: navigator.userAgent,
        is_active: true,
      };

      console.log("📝 Session data prepared:", JSON.stringify(sessionData, null, 2));

      console.log("💾 Inserting into database...");
      const { data, error } = await supabase
        .from("user_sessions")
        .insert(sessionData)
        .select()
        .single();

      console.log("📦 Database response:", { data, error });

      if (error) {
        console.error("❌ DATABASE ERROR:", error);
        console.error("❌ Error message:", error.message);
        console.error("❌ Error details:", error.details);
        console.error("❌ Error hint:", error.hint);
        console.error("❌ Error code:", error.code);
        throw error;
      }

      if (!data) {
        console.error("❌ No data returned from insert!");
        return null;
      }

      this.currentSessionId = data.id;
      console.log("✅ SUCCESS! Session created with ID:", this.currentSessionId);
      console.log("✅ Full session data:", data);
      return data;
    } catch (error) {
      console.error("❌ EXCEPTION in createSession:", error);
      console.error("❌ Error name:", error.name);
      console.error("❌ Error message:", error.message);
      console.error("❌ Error stack:", error.stack);
      return null;
    }
  }

  // Update session activity (call this periodically)
  async updateSessionActivity() {
    if (!this.currentSessionId) {
      // Don't log this every time - it's too noisy
      return;
    }

    try {
      const { error } = await supabase
        .from("user_sessions")
        .update({ last_activity: new Date().toISOString() })
        .eq("id", this.currentSessionId);

      if (error) {
        console.error("Error updating session activity:", error);
        throw error;
      }
    } catch (error) {
      console.error("Failed to update session activity:", error);
    }
  }

  // End session when user logs out
  async endSession() {
    console.log("🔴 endSession called");
    console.log("🔴 currentSessionId:", this.currentSessionId);
    
    if (!this.currentSessionId) {
      console.log("⚠️ No active session to end");
      return;
    }

    const sessionToEnd = this.currentSessionId;
    console.log("🔴 Ending session:", sessionToEnd);

    try {
      const { error } = await supabase
        .from("user_sessions")
        .update({
          session_end: new Date().toISOString(),
          is_active: false,
        })
        .eq("id", sessionToEnd);

      if (error) {
        console.error("❌ Error ending session in database:", error);
        throw error;
      }

      console.log("✅ Session ended successfully:", sessionToEnd);
    } catch (error) {
      console.error("❌ Failed to end session:", error);
    } finally {
      this.currentSessionId = null;
      console.log("🔴 Session ID cleared");
    }
  }

  // Get user's IP address (using a public API)
  async getIPAddress() {
    try {
      const response = await fetch("https://api.ipify.org?format=json", {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      const data = await response.json();
      console.log("🌐 IP address fetched:", data.ip);
      return data.ip;
    } catch (error) {
      console.error("⚠️ Error fetching IP (continuing anyway):", error);
      return null;
    }
  }

  // Get all sessions for current user
  async getUserSessions(userId) {
    try {
      const { data, error } = await supabase
        .from("user_sessions")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching user sessions:", error);
      return [];
    }
  }

  // End all other active sessions (useful for "logout from all devices")
  async endOtherSessions(userId) {
    if (!this.currentSessionId) {
      console.log("No current session ID to compare against");
      return;
    }

    try {
      const { error } = await supabase
        .from("user_sessions")
        .update({
          session_end: new Date().toISOString(),
          is_active: false,
        })
        .eq("user_id", userId)
        .eq("is_active", true)
        .neq("id", this.currentSessionId);

      if (error) throw error;
      console.log("✅ Other sessions ended for user:", userId);
    } catch (error) {
      console.error("❌ Error ending other sessions:", error);
    }
  }
}

export const sessionService = new SessionService();